import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { GalleryTag } from '../types'
import { createTag, addTagToGallery, removeTagFromGallery, fetchTags } from '../api/galleryApi'

/**
 * Tag hooks
 *
 * TODOs:
 * - [ ] Add `useTags()` query to list all tags and cache them for pickers
 * - [ ] Add search/suggest helpers for tag inputs
 * - [ ] Add optimistic update on add/remove for better UX
 */

export function useCreateTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ name, description }: { name: string; description?: string }) => createTag(name, description),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gallery-tags'] })
    }
  })
}

export function useTags() {
  // Fetch all available tags for pickers; cached across app
  return useQuery<GalleryTag[]>({
    queryKey: ['gallery-tags'],
    queryFn: fetchTags,
    staleTime: 5 * 60_000
  })
}

export function useTagRelations(galleryId: number) {
  const qc = useQueryClient()
  const add = useMutation({
    mutationFn: ({ tagId }: { tagId: string }) => addTagToGallery(galleryId, tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gallery', galleryId] })
      qc.invalidateQueries({ queryKey: ['galleries'] })
    }
  })
  const remove = useMutation({
    mutationFn: ({ tagId }: { tagId: string }) => removeTagFromGallery(galleryId, tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gallery', galleryId] })
      qc.invalidateQueries({ queryKey: ['galleries'] })
    }
  })
  return { add, remove }
}
